/**
 * A simplified LAS file loader that correctly handles common LAS formats
 */
class LASLoader {
  constructor() {
    this.debug = true; // Enable debug logging for troubleshooting
    console.log("LASLoader initialized with debug=true");
  }

  /**
   * Loads a LAS file and processes the points with chunk support
   * @param {string} url - URL to the LAS file
   * @param {function} onLoad - Callback when loaded with points array or chunk
   * @param {function} onProgress - Optional progress callback
   * @param {function} onError - Optional error callback
   * @param {Object} options - Optional loading options
   * @param {boolean} options.useChunks - Whether to load in chunks
   * @param {number} options.chunkSize - Size of each chunk
   * @param {number} options.chunkIndex - Current chunk index to load
   */
  load(url, onLoad, onProgress, onError, options = {}) {
    // Store the URL for use in determining file-specific settings
    this.url = url;
    
    // Set up chunking options
    const useChunks = options.useChunks || false;
    const chunkSize = options.chunkSize || 5000;
    const chunkIndex = options.chunkIndex || 0;
    
    if (this.debug) {
      console.log(`Loading LAS file: ${url}`);
      if (useChunks) {
        console.log(`Using chunk loading: chunk ${chunkIndex}, size ${chunkSize}`);
      }
    }
    
    // Check for node.js environment
    const isNode = typeof window === 'undefined' || typeof XMLHttpRequest === 'undefined';
    
    if (isNode) {
      // Node.js environment - warn that this method needs actual data
      console.warn('In Node.js environment, use parseLAS() directly with file data');
      return onLoad(this.generateFallbackPoints());
    }
    
    // If we already have the buffer cached, use it directly
    if (useChunks && this.cachedBuffer) {
      try {
        this.processChunk(this.cachedBuffer, chunkIndex, chunkSize, onLoad);
        return;
      } catch (err) {
        console.error('Error processing cached buffer chunk:', err);
        // Continue to reload the file if cache processing fails
      }
    }
    
    // Browser environment - use XMLHttpRequest
    const xhr = new XMLHttpRequest();
    xhr.open('GET', url, true);
    xhr.responseType = 'arraybuffer';
    
    xhr.onload = () => {
      if (xhr.status === 200 || xhr.status === 0) {
        if (this.debug) console.log(`LAS file loaded, size: ${xhr.response.byteLength} bytes`);
        
        try {
          const buffer = xhr.response;
          
          // Check buffer size
          if (!buffer || buffer.byteLength < 227) {
            console.error(`LAS file too small or empty: ${buffer ? buffer.byteLength : 0} bytes`);
            return onLoad(this.generateFallbackPoints());
          }
          
          // Cache the buffer for future chunk requests
          if (useChunks) {
            this.cachedBuffer = buffer;
            this.processChunk(buffer, chunkIndex, chunkSize, onLoad);
          } else {
            // Process the entire file at once
            const points = this.parseLAS(buffer);
            onLoad(points);
          }
        } catch (err) {
          console.error('Error processing LAS file:', err);
          onLoad(this.generateFallbackPoints());
          if (onError) onError(err);
        }
      } else {
        console.error(`Failed to load LAS file: ${xhr.status}`);
        onLoad(this.generateFallbackPoints());
        if (onError) onError(new Error(`Failed to load ${url}: ${xhr.status}`));
      }
    };
    
    xhr.onprogress = (event) => {
      if (this.debug && event.lengthComputable) {
        console.log(`LAS loading progress: ${Math.round(event.loaded / event.total * 100)}%`);
      }
      if (onProgress) onProgress(event);
    };
    
    xhr.onerror = (err) => {
      console.error('Error loading LAS file:', err);
      onLoad(this.generateFallbackPoints());
      if (onError) onError(err);
    };
    
    // Skip sending the request if we already have the buffer cached
    if (useChunks && this.cachedBuffer) {
      try {
        this.processChunk(this.cachedBuffer, chunkIndex, chunkSize, onLoad);
      } catch (err) {
        console.error('Error processing cached buffer:', err);
        xhr.send(); // Fallback to sending the request
      }
    } else {
      xhr.send();
    }
  }
  
  /**
   * Process a specific chunk of the LAS file
   * @param {ArrayBuffer} buffer - The LAS file buffer
   * @param {number} chunkIndex - Index of the chunk to process
   * @param {number} chunkSize - Size of each chunk
   * @param {function} onLoad - Callback with chunk points
   */
  processChunk(buffer, chunkIndex, chunkSize, onLoad) {
    try {
      const view = new DataView(buffer);
      const header = this.parseHeader(view);
      
      if (this.debug) {
        console.log(`Processing chunk ${chunkIndex} of size ${chunkSize}`);
      }
      
      const totalPoints = header.numberOfPointRecords;
      const startPoint = chunkIndex * chunkSize;
      const endPoint = Math.min(startPoint + chunkSize, totalPoints);
      
      if (startPoint >= totalPoints) {
        // No more points to process
        onLoad({ 
          points: [], 
          chunkIndex, 
          totalChunks: Math.ceil(totalPoints / chunkSize),
          isLastChunk: true,
          header
        });
        return;
      }
      
      const chunkPoints = this.processPointRange(view, header, startPoint, endPoint);
      
      onLoad({ 
        points: chunkPoints, 
        chunkIndex, 
        totalChunks: Math.ceil(totalPoints / chunkSize),
        isLastChunk: endPoint >= totalPoints,
        header
      });
    } catch (err) {
      console.error('Error processing chunk:', err);
      onLoad({ 
        points: this.generateFallbackPoints(),
        error: err.message
      });
    }
  }
  
  /**
   * Process a specific range of points from the LAS file
   * @param {DataView} view - DataView of the LAS file buffer
   * @param {Object} header - Parsed header information
   * @param {number} startPoint - Index of the first point to process
   * @param {number} endPoint - Index of the point after the last one to process
   * @returns {Array} Array of processed points in the range
   */
  processPointRange(view, header, startPoint, endPoint) {
    const points = [];
    
    // Get offsets and scales from header
    const [xScale, yScale, zScale] = header.scale;
    const [xOffset, yOffset, zOffset] = header.offset;
    
    // Format-specific processing
    const recordLength = header.pointDataRecordLength;
    const dataOffset = header.pointDataOffset;
    
    // Process the specified range of points
    for (let i = startPoint; i < endPoint; i++) {
      const offset = dataOffset + (i * recordLength);
      
      // Ensure we don't read past the buffer
      if (offset + 12 > view.byteLength) break;
      
      try {
        // Read XYZ coordinates (standard for all LAS formats)
        const x = view.getInt32(offset, true) * xScale + xOffset;
        const y = view.getInt32(offset + 4, true) * yScale + yOffset;
        const z = view.getInt32(offset + 8, true) * zScale + zOffset;
        
        // Read intensity if available (2 bytes after XYZ)
        let intensity = 0;
        if (offset + 14 <= view.byteLength) {
          intensity = view.getUint16(offset + 12, true);
        }
        
        // Read classification based on point format
        let classification = 0;
        if (header.pointDataFormat <= 5) {
          // LAS 1.0-1.3 (Formats a.userID0-5)
          // Classification is at byte offset 15 (0-based)
          // Bits 0-4: Classification value (0-31)
          if (offset + 15 <= view.byteLength) {
            classification = view.getUint8(offset + 15) & 0x1F; // Mask out the flags (bits 5-7)
          }
        } else {
          // LAS 1.4 (Formats 6-10)
          // Classification is a full byte at offset 16 (0-based)
          if (offset + 16 <= view.byteLength) {
            classification = view.getUint8(offset + 16); // Full byte value (0-255)
          }
        }
        
        // Color handling based on point format
        let color = null;
        
        try {
          // Following the C++ implementation logic for LAS file RGB values
          
          // Determine the color position based on point format
          // For LAS file with point format 2, 3, 5, 7, 8 that have RGB values
          if (header.pointDataFormat === 2 || 
              header.pointDataFormat === 3 || 
              header.pointDataFormat === 5 ||
              header.pointDataFormat === 7 || 
              header.pointDataFormat === 8) {
            
            // Calculate RGB position based on format according to LAS spec
            let rgbOffset = offset;
            
            // For Format 2 (XYZ + Intensity + Return + RGB)
            // Colors come after: XYZ (12) + Intensity (2) + Return Number/Flags (1) + 
            // Classification (1) + Scan Angle (1) + User Data (1) + Point Source ID (2) = 20 bytes
            if (header.pointDataFormat === 2) {
              rgbOffset += 20;
            }
            
            // For Format 3 (XYZ + Intensity + Return + RGB + GPS Time)
            // Colors come after: XYZ (12) + Intensity (2) + Return Number/Flags (1) + 
            // Classification (1) + Scan Angle (1) + User Data (1) + Point Source ID (2) = 20 bytes
            else if (header.pointDataFormat === 3) {
              // Dana Point LAS files have RGB at offset +20
              if (this.url && (this.url.includes("dp2.las") || this.url.includes("DP-strip.las"))) {
                rgbOffset += 20;
                if (this.debug && i < 3) {
                  console.log(`Using Dana Point RGB offset: +20`);
                }
              } else {
                rgbOffset += 20; // Standard offset for Format 3
              }
            }
            
            // For Format 7 (LAS 1.4 with RGB)
            // Colors come after: XYZ (12) + Intensity (2) + Return Info (2) + 
            // Classification flags (1) + Classification (1) + User Data (1) + 
            // Scan Angle (2) + Point Source ID (2) + GPS Time (8) = 30 bytes
            else if (header.pointDataFormat === 7 || header.pointDataFormat === 8) {
              rgbOffset += 30;
            }
            
            // Format 5 with RGB + Wave
            // Colors come after: XYZ (12) + Intensity (2) + Return Number/Flags (1) + 
            // Classification (1) + Scan Angle (1) + User Data (1) + Point Source ID (2) + 
            // GPS Time (8) = 28 bytes 
            else if (header.pointDataFormat === 5) {
              rgbOffset += 28;
            }
            
            // Make sure we have enough data to read colors
            if (rgbOffset + 6 <= view.byteLength) {
              // Extract RGB values (16-bit per channel)
              // According to LAS spec - RGB values are 16-bit values in the order R,G,B
              // Some implementations (like Dana Point) may use different byte order
              let rRaw, gRaw, bRaw, r, g, b;
              
              // Special case for Dana Point LAS files - known to use BGR ordering
              if (this.url && (this.url.includes("dp2.las") || this.url.includes("DP-strip.las")) && header.pointDataFormat === 3) {
                // Dana Point stores colors as BGR at offset +20
                // Read raw values from the file
                bRaw = view.getUint16(rgbOffset, true);     // B at offset +20
                gRaw = view.getUint16(rgbOffset + 2, true); // G at offset +22
                rRaw = view.getUint16(rgbOffset + 4, true); // R at offset +24
                
                if (this.debug && i < 5) {
                  console.log(`Dana Point BGR values: B=${bRaw}, G=${gRaw}, R=${rRaw}`);
                }
              }
              // Standard case for LAS files following the spec (RGB order)
              else {
                // Standard RGB reading - LAS spec defines RGB order
                rRaw = view.getUint16(rgbOffset, true);     // R
                gRaw = view.getUint16(rgbOffset + 2, true); // G
                bRaw = view.getUint16(rgbOffset + 4, true); // B
              }
              
              // Detect color encoding - LAS spec says 16-bit, but some implementations use 8-bit values
              const maxColorValue = Math.max(rRaw, gRaw, bRaw);
              
              // If all values are very small (≤ 255), likely 8-bit values stored in 16-bit fields
              if (maxColorValue <= 255) {
                // 8-bit values (stored in 16-bit fields)
                r = rRaw / 255;
                g = gRaw / 255;
                b = bRaw / 255;
                
                if (this.debug && i < 3) {
                  console.log(`Using 8-bit color scaling (0-255) for point ${i}`);
                }
              } else {
                // 16-bit values as per LAS specification
                r = rRaw / 65535;
                g = gRaw / 65535;
                b = bRaw / 65535;
                
                if (this.debug && i < 3) {
                  console.log(`Using 16-bit color scaling (0-65535) for point ${i}`);
                }
              }
              
              if (this.debug && i < 5) {
                console.log(`Normalized RGB: R=${r.toFixed(4)}, G=${g.toFixed(4)}, B=${b.toFixed(4)}`);
              }
              
              // Ensure values are valid
              r = Math.max(0, Math.min(1, isNaN(r) ? 0 : r));
              g = Math.max(0, Math.min(1, isNaN(g) ? 0 : g));
              b = Math.max(0, Math.min(1, isNaN(b) ? 0 : b));
              
              // Store the processed color values
              color = { r, g, b };
            }
          }
          
          // If we couldn't extract color from standard positions 
          // and debugging is enabled, use classification as fallback
          if (!color && offset + 15 <= view.byteLength) {
            const classification = view.getUint8(offset + 15) & 0x1F;
            const r = (classification & 0x07) / 7; // 3 bits for red
            const g = ((classification >> 3) & 0x03) / 3; // 2 bits for green 
            const b = ((classification >> 5) & 0x03) / 3; // 2 bits for blue
            
            color = { r, g, b };
            
            if (this.debug && i < 5) {
              console.log(`Using classification-based color: ${r.toFixed(3)}, ${g.toFixed(3)}, ${b.toFixed(3)}`);
            }
          }
        } catch (e) {
          if (this.debug) console.warn("Failed to read RGB values:", e);
        }
        
        points.push({
          x, y, z,
          intensity,
          classification,
          color,
          index: i // Add index for reference
        });
      } catch (e) {
        if (this.debug) console.warn(`Error reading point at offset ${offset}:`, e);
      }
    }
    
    if (this.debug) {
      console.log(`Processed ${points.length} points from chunk`);
    }
    
    return points;
  }

  /**
   * Parse LAS file
   * @param {ArrayBuffer} buffer - The LAS file buffer
   * @returns {Array} Array of points with x, y, z coordinates
   */
  parseLAS(buffer) {
    const view = new DataView(buffer);
    
    try {
      // Parse the header
      const header = this.parseHeader(view);
      
      if (this.debug) {
        console.log('LAS Header:', header);
      }
      
      // Validate the header
      if (!header.pointDataOffset || header.pointDataOffset >= buffer.byteLength) {
        throw new Error(`Invalid point data offset: ${header.pointDataOffset}`);
      }
      
      if (header.pointDataFormat > 10) {
        console.warn(`Point data format ${header.pointDataFormat} may not be fully supported`);
      }
      
      // Process points based on header information
      return this.processPoints(view, header);
    } catch (error) {
      console.error('Error parsing LAS file:', error);
      throw error;
    }
  }
  
  /**
   * Parse the LAS file header
   * @param {DataView} view - DataView of the LAS file buffer
   * @returns {Object} Header information
   */
  parseHeader(view) {
    // Read file signature (first 4 bytes)
    const signature = String.fromCharCode(
      view.getUint8(0), view.getUint8(1), view.getUint8(2), view.getUint8(3)
    );
    
    if (signature !== 'LASF') {
      throw new Error(`Not a valid LAS file (signature: ${signature})`);
    }
    
    // Read version
    const versionMajor = view.getUint8(24);
    const versionMinor = view.getUint8(25);
    
    console.log(`LAS Version: ${versionMajor}.${versionMinor}`);
    
    // Get header size
    const headerSize = view.getUint16(94, true);
    
    // Get point data format and length
    const pointDataFormat = view.getUint8(104);
    const pointDataRecordLength = view.getUint16(105, true);
    
    console.log(`Point data format: ${pointDataFormat}, record length: ${pointDataRecordLength}`);
    
    // Let's examine more header information to help debug color offsets
    if (this.debug) {
      console.log(`Header size: ${headerSize} bytes`);
      
      // Get global encoding flags
      const globalEncoding = view.getUint16(6, true);
      console.log(`Global encoding: ${globalEncoding.toString(2).padStart(16, '0')}`);
      
      // Read the system identifier and generating software
      let systemId = '';
      for (let i = 0; i < 32; i++) {
        const char = view.getUint8(26 + i);
        if (char !== 0) systemId += String.fromCharCode(char);
      }
      
      let generatingSw = '';
      for (let i = 0; i < 32; i++) {
        const char = view.getUint8(58 + i);
        if (char !== 0) generatingSw += String.fromCharCode(char);
      }
      
      console.log(`System identifier: ${systemId}`);
      console.log(`Generating software: ${generatingSw}`);
      
      // Try to determine point format details
      let formatDescription = "unknown format";
      if (pointDataFormat === 0) formatDescription = "basic XYZ only";
      if (pointDataFormat === 1) formatDescription = "XYZ + intensity + return info";
      if (pointDataFormat === 2) formatDescription = "XYZ + intensity + return info + RGB";
      if (pointDataFormat === 3) formatDescription = "XYZ + intensity + return info + RGB + time";
      if (pointDataFormat === 4) formatDescription = "XYZ + intensity + return info + time + wave packets";
      if (pointDataFormat === 5) formatDescription = "XYZ + intensity + return info + RGB + time + wave packets";
      if (pointDataFormat === 6) formatDescription = "LAS 1.4 basic";
      if (pointDataFormat === 7) formatDescription = "LAS 1.4 with RGB";
      if (pointDataFormat === 8) formatDescription = "LAS 1.4 with RGB + NIR";
      
      console.log(`Point format ${pointDataFormat}: ${formatDescription}`);
      console.log(`Point record length: ${pointDataRecordLength} bytes`);
      
      // Sample the first point record to help debug
      const firstPointOffset = view.getUint32(96, true); // Point data offset
      console.log(`First point record starts at byte ${firstPointOffset}`);
      
      // Print some bytes from the first point record to analyze structure
      if (firstPointOffset < view.byteLength) {
        let bytes = [];
        for (let i = 0; i < Math.min(40, pointDataRecordLength); i++) {
          if (firstPointOffset + i < view.byteLength) {
            bytes.push(view.getUint8(firstPointOffset + i).toString(16).padStart(2, '0'));
          }
        }
        console.log(`First point record bytes: ${bytes.join(' ')}`);
      }
    }
    
    // Get number of point records (depends on version)
    let numberOfPointRecords;
    if (versionMajor >= 1 && versionMinor >= 4) {
      // LAS 1.4 uses 64-bit count at a different offset
      numberOfPointRecords = view.getBigUint64(247, true);
    } else {
      // LAS 1.0-1.3 uses 32-bit count
      numberOfPointRecords = view.getUint32(107, true);
    }
    
    // Get point data offset
    const pointDataOffset = view.getUint32(96, true);
    
    // Get scales and offsets
    const xScale = view.getFloat64(131, true);
    const yScale = view.getFloat64(139, true);
    const zScale = view.getFloat64(147, true);
    
    const xOffset = view.getFloat64(155, true);
    const yOffset = view.getFloat64(163, true);
    const zOffset = view.getFloat64(171, true);
    
    // Get min/max values - note: LAS spec has different order than our code expects
    const minX = view.getFloat64(179, true);
    const maxX = view.getFloat64(187, true);
    const minY = view.getFloat64(195, true);
    const maxY = view.getFloat64(203, true);
    const minZ = view.getFloat64(211, true);
    const maxZ = view.getFloat64(219, true);
    
    // Get the number of VLRs for potential compression or other metadata
    const numberOfVLRs = view.getUint32(100, true);
    
    return {
      signature,
      version: `${versionMajor}.${versionMinor}`,
      versionMajor,
      versionMinor,
      headerSize,
      pointDataFormat,
      pointDataRecordLength,
      numberOfPointRecords: Number(numberOfPointRecords), // Convert BigInt to Number if needed
      pointDataOffset,
      numberOfVLRs,
      scale: [xScale, yScale, zScale],
      offset: [xOffset, yOffset, zOffset],
      min: [minX, minY, minZ],
      max: [maxX, maxY, maxZ],
      bounds: {
        min: { x: minX, y: minY, z: minZ },
        max: { x: maxX, y: maxY, z: maxZ }
      }
    };
  }

  /**
   * Process point data from the LAS file
   * @param {DataView} view - DataView of the LAS file buffer
   * @param {Object} header - Parsed header information
   * @returns {Array} Array of point objects
   */
  processPoints(view, header) {
    const MAX_POINTS = 500000; // Limit points for performance
    
    // Calculate number of points to read with a stride
    const totalPoints = header.numberOfPointRecords;
    
    // Use stride to sample points if there are too many
    let stride = 1;
    if (totalPoints > MAX_POINTS) {
      stride = Math.ceil(totalPoints / MAX_POINTS);
      console.log(`Sampling points with stride ${stride} (total: ${totalPoints})`);
      
      // Create an array of indices to process with stride
      const indices = [];
      for (let i = 0; i < totalPoints; i += stride) {
        indices.push(i);
      }
      
      // Process these points
      const points = this.processPointRange(view, header, 0, totalPoints, indices);
      
      console.log(`Successfully processed ${points.length} points from LAS file`);
      
      // Return points, or fallback if none were read
      return points.length > 0 ? points : this.generateFallbackPoints();
    } else {
      // If we're under the limit, process all points
      const points = this.processPointRange(view, header, 0, totalPoints);
      
      console.log(`Successfully processed ${points.length} points from LAS file`);
      
      // Return points, or fallback if none were read
      return points.length > 0 ? points : this.generateFallbackPoints();
    }
  }
  
  /**
   * Process a specific range of points from the LAS file
   * @param {DataView} view - DataView of the LAS file buffer
   * @param {Object} header - Parsed header information
   * @param {number} startPoint - Index of the first point to process
   * @param {number} endPoint - Index of the point after the last one to process
   * @param {Array} indices - Optional array of specific indices to process
   * @returns {Array} Array of processed points in the range
   */
  processPointRange(view, header, startPoint, endPoint, indices = null) {
    const points = [];
    
    // Get offsets and scales from header
    const [xScale, yScale, zScale] = header.scale;
    const [xOffset, yOffset, zOffset] = header.offset;
    
    // Format-specific processing
    const recordLength = header.pointDataRecordLength;
    const dataOffset = header.pointDataOffset;
    
    // Process the specified range of points
    if (indices) {
      // Process only the specified indices
      for (const i of indices) {
        if (i >= startPoint && i < endPoint) {
          const offset = dataOffset + (i * recordLength);
          
          if (this.processPointAt(view, header, offset, i, xScale, yScale, zScale, 
                                xOffset, yOffset, zOffset, points)) {
            // Point successfully processed and added to points array
          }
        }
      }
    } else {
      // Process all points in the range
      for (let i = startPoint; i < endPoint; i++) {
        const offset = dataOffset + (i * recordLength);
        
        if (this.processPointAt(view, header, offset, i, xScale, yScale, zScale, 
                              xOffset, yOffset, zOffset, points)) {
          // Point successfully processed and added to points array
        }
      }
    }
    
    if (this.debug) {
      console.log(`Processed ${points.length} points from range ${startPoint}-${endPoint}`);
    }
    
    return points;
  }
  
  /**
   * Process a single point at the specified offset
   * @param {DataView} view - DataView of the LAS file buffer
   * @param {Object} header - Parsed header information
   * @param {number} offset - Byte offset of the point in the buffer
   * @param {number} i - Index of the point
   * @param {number} xScale - X scale factor
   * @param {number} yScale - Y scale factor
   * @param {number} zScale - Z scale factor
   * @param {number} xOffset - X offset value
   * @param {number} yOffset - Y offset value
   * @param {number} zOffset - Z offset value
   * @param {Array} points - Array to add the processed point to
   * @returns {boolean} True if point was successfully processed, false otherwise
   */
  processPointAt(view, header, offset, i, xScale, yScale, zScale, xOffset, yOffset, zOffset, points) {
    // Ensure we don't read past the buffer
    if (offset + 12 > view.byteLength) return false;
    
    try {
      // Read XYZ coordinates (standard for all LAS formats)
      const x = view.getInt32(offset, true) * xScale + xOffset;
      const y = view.getInt32(offset + 4, true) * yScale + yOffset;
      const z = view.getInt32(offset + 8, true) * zScale + zOffset;
      
      // Read intensity if available (2 bytes after XYZ)
      let intensity = 0;
      if (offset + 14 <= view.byteLength) {
        intensity = view.getUint16(offset + 12, true);
      }
      
      // Read classification based on point format
      let classification = 0;
      if (header.pointDataFormat <= 5) {
        // LAS 1.0-1.3 (Formats 0-5)
        // Classification is at byte offset 15 (0-based)
        // Bits 0-4: Classification value (0-31)
        if (offset + 15 <= view.byteLength) {
          classification = view.getUint8(offset + 15) & 0x1F; // Mask out the flags (bits 5-7)
        }
      } else {
        // LAS 1.4 (Formats 6-10)
        // Classification is a full byte at offset 16 (0-based)
        if (offset + 16 <= view.byteLength) {
          classification = view.getUint8(offset + 16); // Full byte value (0-255)
        }
      }
      
      // Color handling based on point format
      let color = null;
      
      try {
        // Following the C++ implementation logic for LAS file RGB values
        
        // Determine the color position based on point format
        // For LAS file with point format 2, 3, 5, 7, 8 that have RGB values
        if (header.pointDataFormat === 2 || 
            header.pointDataFormat === 3 || 
            header.pointDataFormat === 5 ||
            header.pointDataFormat === 7 || 
            header.pointDataFormat === 8) {
          
          // Calculate RGB position based on format according to LAS spec
          let rgbOffset = offset;
          
          // For Format 2 (XYZ + Intensity + Return + RGB)
          // Colors come after: XYZ (12) + Intensity (2) + Return Number/Flags (1) + 
          // Classification (1) + Scan Angle (1) + User Data (1) + Point Source ID (2) = 20 bytes
          if (header.pointDataFormat === 2) {
            rgbOffset += 20;
          }
          
          // For Format 3 (XYZ + Intensity + Return + RGB + GPS Time)
          // Colors come after: XYZ (12) + Intensity (2) + Return Number/Flags (1) + 
          // Classification (1) + Scan Angle (1) + User Data (1) + Point Source ID (2) = 20 bytes
          else if (header.pointDataFormat === 3) {
            // Dana Point LAS files have RGB at offset +20
            if (this.url && (this.url.includes("dp2.las") || this.url.includes("DP-strip.las"))) {
              rgbOffset += 20;
              if (this.debug && i < 3) {
                console.log(`Using Dana Point RGB offset: +20`);
              }
            } else {
              rgbOffset += 20; // Standard offset for Format 3
            }
          }
          
          // For Format 7 (LAS 1.4 with RGB)
          // Colors come after: XYZ (12) + Intensity (2) + Return Info (2) + 
          // Classification flags (1) + Classification (1) + User Data (1) + 
          // Scan Angle (2) + Point Source ID (2) + GPS Time (8) = 30 bytes
          else if (header.pointDataFormat === 7 || header.pointDataFormat === 8) {
            rgbOffset += 30;
          }
          
          // Format 5 with RGB + Wave
          // Colors come after: XYZ (12) + Intensity (2) + Return Number/Flags (1) + 
          // Classification (1) + Scan Angle (1) + User Data (1) + Point Source ID (2) + 
          // GPS Time (8) = 28 bytes 
          else if (header.pointDataFormat === 5) {
            rgbOffset += 28;
          }
          
          // Make sure we have enough data to read colors
          if (rgbOffset + 6 <= view.byteLength) {
            // Extract RGB values (16-bit per channel)
            // According to LAS spec - RGB values are 16-bit values in the order R,G,B
            // Some implementations (like Dana Point) may use different byte order
            let rRaw, gRaw, bRaw, r, g, b;
            
            // Special case for Dana Point LAS files - known to use BGR ordering
            if (this.url && (this.url.includes("dp2.las") || this.url.includes("DP-strip.las")) && header.pointDataFormat === 3) {
              // Dana Point stores colors as BGR at offset +20
              // Read raw values from the file
              bRaw = view.getUint16(rgbOffset, true);     // B at offset +20
              gRaw = view.getUint16(rgbOffset + 2, true); // G at offset +22
              rRaw = view.getUint16(rgbOffset + 4, true); // R at offset +24
              
              if (this.debug && i < 5) {
                console.log(`Dana Point BGR values: B=${bRaw}, G=${gRaw}, R=${rRaw}`);
              }
            }
            // Standard case for LAS files following the spec (RGB order)
            else {
              // Standard RGB reading - LAS spec defines RGB order
              rRaw = view.getUint16(rgbOffset, true);     // R
              gRaw = view.getUint16(rgbOffset + 2, true); // G
              bRaw = view.getUint16(rgbOffset + 4, true); // B
            }
            
            // Detect color encoding - LAS spec says 16-bit, but some implementations use 8-bit values
            const maxColorValue = Math.max(rRaw, gRaw, bRaw);
            
            // If all values are very small (≤ 255), likely 8-bit values stored in 16-bit fields
            if (maxColorValue <= 255) {
              // 8-bit values (stored in 16-bit fields)
              r = rRaw / 255;
              g = gRaw / 255;
              b = bRaw / 255;
              
              if (this.debug && i < 3) {
                console.log(`Using 8-bit color scaling (0-255) for point ${i}`);
              }
            } else {
              // 16-bit values as per LAS specification
              r = rRaw / 65535;
              g = gRaw / 65535;
              b = bRaw / 65535;
              
              if (this.debug && i < 3) {
                console.log(`Using 16-bit color scaling (0-65535) for point ${i}`);
              }
            }
            
            if (this.debug && i < 5) {
              console.log(`Normalized RGB: R=${r.toFixed(4)}, G=${g.toFixed(4)}, B=${b.toFixed(4)}`);
            }
            
            // Ensure values are valid
            r = Math.max(0, Math.min(1, isNaN(r) ? 0 : r));
            g = Math.max(0, Math.min(1, isNaN(g) ? 0 : g));
            b = Math.max(0, Math.min(1, isNaN(b) ? 0 : b));
            
            // Store the processed color values
            color = { r, g, b };
          }
        }
        
        // If we couldn't extract color from standard positions 
        // and debugging is enabled, use classification as fallback
        if (!color && offset + 15 <= view.byteLength) {
          const classVal = view.getUint8(offset + 15) & 0x1F;
          const r = (classVal & 0x07) / 7; // 3 bits for red
          const g = ((classVal >> 3) & 0x03) / 3; // 2 bits for green 
          const b = ((classVal >> 5) & 0x03) / 3; // 2 bits for blue
          
          color = { r, g, b };
          
          if (this.debug && i < 5) {
            console.log(`Using classification-based color: ${r.toFixed(3)}, ${g.toFixed(3)}, ${b.toFixed(3)}`);
          }
        }
      } catch (e) {
        if (this.debug) console.warn("Failed to read RGB values:", e);
      }
      
      points.push({
        x, y, z,
        intensity,
        classification,
        color,
        index: i // Add index for reference
      });
      
      return true;
    } catch (e) {
      if (this.debug) console.warn(`Error reading point at offset ${offset}:`, e);
      return false;
    }
  }
  
  /**
   * Generates synthetic points for testing or fallback
   * @returns {Array} Array of point objects
   */
  generateFallbackPoints() {
    console.warn("Using fallback point generator");
    const points = [];
    
    // Generate a grid of points (smaller grid for better performance)
    const gridSize = 50;
    const spacing = 0.2;
    
    for (let x = 0; x < gridSize; x++) {
      for (let z = 0; z < gridSize; z++) {
        // Create a heightmap using sine waves
        const height = 
          Math.sin(x * spacing * 0.5) * 0.5 + 
          Math.cos(z * spacing * 0.3) * 0.5;
        
        points.push({
          x: x * spacing - (gridSize * spacing) / 2,
          y: height,
          z: z * spacing - (gridSize * spacing) / 2,
          intensity: Math.floor(height * 255),
          classification: 0,
          color: null
        });
      }
    }
    
    return points;
  }
}

// Support both ESM and CommonJS
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { LASLoader };
} else {
  // ESM export
  if (typeof window !== 'undefined') {
    window.LASLoader = LASLoader;
  }
}

// For direct import in ESM
export { LASLoader };